// Ranking de vendedores cruzando el padrón de `retail-org` con las KPIs del periodo del wizard.
// Solo lo usa el Home; Vendedores lee `/backoffice/sellers/table`.
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { sellerKpisApi } from '@/api/services/retail-org/backoffice';
import { useGoals } from '../../lib/goals';
import { mergeSellerRoster, zoneNameById } from '../../lib/roster';
import {
    useRosterRetailer,
    useRosterStores,
    useRosterUsers,
    useRosterZones,
} from '../../lib/useRoster';
import { usePeriodWindow, withKpiGoal, type KpiPeriod } from '../../tiendas/api/useTiendas';

const KEY = ['retail-admin', 'vendedores'] as const;

/**
 * `rosterOptional`: si el padrón falla, lista solo las KPIs en vez de tumbar la pantalla (Home).
 * `enabled: false` no pide nada.
 */
export function useSellers(period: KpiPeriod, { rosterOptional = false, enabled = true } = {}) {
    const periodWindow = usePeriodWindow(period);
    const kpis = useQuery({
        queryKey: [...KEY, 'list', periodWindow],
        queryFn: () => sellerKpisApi.listSellers(periodWindow),
        staleTime: 60_000,
        enabled,
    });
    const { retailerId: rosterRetailerId, problemError } = useRosterRetailer();
    const retailerId = enabled ? rosterRetailerId : null;
    const users = useRosterUsers(retailerId);
    const stores = useRosterStores(retailerId);
    const zones = useRosterZones(retailerId);
    const goals = useGoals('SELLER');

    const rosterFailed = problemError !== null || users.isError || stores.isError;
    const skipRoster = rosterOptional && rosterFailed;

    const data = useMemo(() => {
        const roster = skipRoster
            ? { users: [], stores: [] }
            : { users: users.data, stores: stores.data };
        if (!kpis.data || !roster.users || !roster.stores) return undefined;
        const rows = mergeSellerRoster(
            roster.users,
            kpis.data.items,
            roster.stores,
            zoneNameById(zones.data ?? []),
        );
        return {
            items: withKpiGoal(rows, (row) => row.sellerId, goals.data?.goals, period),
            truncated: kpis.data.truncated,
        };
    }, [kpis.data, users.data, stores.data, skipRoster, zones.data, goals.data, period]);

    // Campos explícitos y no `{ ...query }`: el spread suscribe a todo el query y re-renderiza de más.
    const error = skipRoster
        ? kpis.error
        : (problemError ?? kpis.error ?? users.error ?? stores.error);
    return {
        data,
        error,
        goalsError: goals.error,
        isPending:
            error === null &&
            (kpis.isPending || (!skipRoster && (users.isPending || stores.isPending))),
        isError: error !== null,
        refetch: () => {
            void kpis.refetch();
            void users.refetch();
            void stores.refetch();
        },
    };
}
